/* ============================================================================
 * SEO.jsx
 * ============================================================================
 * Componente para gestionar meta tags SEO de la aplicación.
 *
 * Responsabilidades:
 * - Configurar título y descripción de la página
 * - Gestionar meta tags Open Graph para redes sociales
 * - Configurar Twitter Cards
 * - Manejar links canónicos y alternate (hreflang)
 * - Establecer el atributo lang del documento
 * - Soportar meta tags adicionales según tipo de contenido
 *
 * @module SEO
 * ========================================================================== */

import { useMemo } from "react";

import { Helmet } from "react-helmet-async";

import { useTranslation } from "react-i18next";

import { isEmpty } from "../../utils/validators";

/* ============================================================================
 * Constantes
 * ========================================================================== */

/**
 * Handle de Twitter de la aplicación.
 * @type {string}
 */
const TWITTER_HANDLE = "@Ydray_";

/**
 * Tipos de contenido Open Graph soportados.
 * @type {Object<string, string>}
 */
const OG_TYPE = {
  /** Páginas generales: home, about, contact, landing pages */
  WEBSITE: "website",
  /** Contenido editorial: blog posts, noticias, guías, tutoriales */
  ARTICLE: "article",
  /** Productos con precio: planes, servicios específicos */
  PRODUCT: "product",
};

/**
 * Tipo de tarjeta de Twitter.
 * @type {Object<string, string>}
 */
const TWITTER_CARD_TYPE = {
  /** Tarjeta pequeña con imagen cuadrada */
  SUMMARY: "summary",
  /** Tarjeta grande con imagen rectangular */
  SUMMARY_LARGE_IMAGE: "summary_large_image",
};

/**
 * Dimensiones de la imagen OG por defecto.
 * @type {Object}
 */
const DEFAULT_IMAGE_DIMENSIONS = {
  width: 1200,
  height: 630,
};

/**
 * Configuración de robots por defecto.
 * @type {string}
 */
const DEFAULT_ROBOTS = "index, follow";

/* ============================================================================
 * Utilidades
 * ========================================================================== */

/**
 * Genera la URL de la imagen OG según el entorno.
 *
 * @returns {string} URL completa de la imagen
 */
const getOgImageUrl = () => {
  return `${process.env.REACT_APP_PUBLIC_URL}/assets/images/logo.png`;
};

/**
 * Convierte código de idioma a formato locale OG.
 *
 * Open Graph requiere el formato ll_CC (idioma_PAÍS) para indicar
 * correctamente el idioma del contenido a las redes sociales.
 *
 * @param {string} language - Código de idioma ("es" o "en")
 * @returns {string} Locale en formato OG (ej: "es_ES", "en_US")
 *
 * @example
 * getOgLocale("es"); // "es_ES"
 * getOgLocale("en"); // "en_US"
 * getOgLocale("fr"); // "en_US" (fallback)
 */
const getOgLocale = (language) => {
  const localeMap = {
    es: "es_ES",
    en: "en_US",
  };

  return localeMap[language] || "en_US";
};

/* ============================================================================
 * SEO Component
 * ========================================================================== */

/**
 * Componente para gestionar meta tags SEO.
 *
 * Configura todas las meta tags necesarias para SEO y compartir en
 * redes sociales (Open Graph y Twitter Cards).
 *
 * @param {Object} props - Props del componente
 * @param {string} props.title - Título de la página
 * @param {string} props.description - Descripción de la página
 * @param {string} props.url - URL de la página
 * @param {string} [props.canonical] - URL canónica (si difiere de url)
 * @param {Array<{lng: string, url: string}>} [props.altLinks] - Links alternativos por idioma
 * @param {string} [props.ogType="website"] - Tipo de contenido OG (website, article, product)
 * @param {string} [props.twitterCard="summary"] - Tipo de tarjeta Twitter
 * @param {string} [props.image] - URL de imagen personalizada
 * @param {string} [props.robots="index, follow"] - Directiva para robots
 * @param {Object} [props.extraMeta] - Meta tags adicionales según tipo de contenido
 * @param {Object} [props.extraMeta.article] - Meta tags para artículos
 * @param {string} [props.extraMeta.article.publishedTime] - Fecha de publicación ISO 8601
 * @param {string} [props.extraMeta.article.modifiedTime] - Fecha de modificación ISO 8601
 * @param {string} [props.extraMeta.article.author] - URL del perfil del autor
 * @param {string} [props.extraMeta.article.section] - Sección/categoría del artículo
 * @param {string[]} [props.extraMeta.article.tags] - Tags/etiquetas del artículo
 * @param {Object} [props.extraMeta.product] - Meta tags para productos
 * @param {number|string} [props.extraMeta.product.price] - Precio del producto
 * @param {string} [props.extraMeta.product.currency] - Moneda (EUR, USD, etc.)
 * @param {string} [props.extraMeta.product.availability] - Disponibilidad (in stock, out of stock)
 * @returns {JSX.Element} Helmet con meta tags
 *
 * @example
 * // Uso básico (website)
 * <SEO
 *   title="Mi Página - YDRAY"
 *   description="Descripción de mi página"
 *   url="https://ydray.com/es/mi-pagina"
 * />
 *
 * @example
 * // Artículo de blog
 * <SEO
 *   title="Cómo enviar archivos grandes - YDRAY"
 *   description="Guía completa para enviar archivos grandes"
 *   url="https://ydray.com/es/blog/enviar-archivos-grandes"
 *   ogType="article"
 *   twitterCard="summary_large_image"
 *   extraMeta={{
 *     article: {
 *       publishedTime: "2024-01-15T09:00:00Z",
 *       modifiedTime: "2024-01-20T14:30:00Z",
 *       author: "https://ydray.com/es/autor/juan",
 *       section: "Tutoriales",
 *       tags: ["archivos", "transferencia", "guía"],
 *     },
 *   }}
 * />
 *
 * @example
 * // Página de producto/plan
 * <SEO
 *   title="Plan Pro - YDRAY"
 *   description="Plan profesional con 50GB de almacenamiento"
 *   url="https://ydray.com/es/planes/pro"
 *   ogType="product"
 *   extraMeta={{
 *     product: {
 *       price: 12.00,
 *       currency: "EUR",
 *       availability: "in stock",
 *     },
 *   }}
 * />
 */
function SEO({
  title,
  description,
  url,
  canonical,
  altLinks,
  ogType = OG_TYPE.WEBSITE,
  twitterCard = TWITTER_CARD_TYPE.SUMMARY,
  image,
  robots = DEFAULT_ROBOTS,
  extraMeta = {},
}) {
  const { t, i18n } = useTranslation();

  /* --------------------------------------------------------------------------
   * Valores derivados
   * ------------------------------------------------------------------------ */

  /**
   * Nombre de la aplicación para meta tags.
   */
  const appName = useMemo(() => t("app.name"), [t]);

  /**
   * Idioma actual en formato locale OG.
   */
  const ogLocale = useMemo(() => {
    return getOgLocale(i18n.resolvedLanguage);
  }, [i18n.resolvedLanguage]);

  /**
   * URL de la imagen OG (personalizada o por defecto).
   */
  const ogImage = useMemo(() => {
    return image || getOgImageUrl();
  }, [image]);

  /**
   * Texto alternativo de la imagen OG.
   */
  const ogImageAlt = useMemo(() => {
    return `${t("app.name")} - ${t("landing.title")}`;
  }, [t]);

  /**
   * URL canónica (usa canonical si existe, sino url).
   */
  const canonicalUrl = useMemo(() => {
    return !isEmpty(canonical) ? canonical : url;
  }, [canonical, url]);

  /**
   * Links alternativos para hreflang.
   */
  const alternateLinks = useMemo(() => {
    if (!altLinks || altLinks.length === 0) {
      return null;
    }

    return altLinks.map((item) => (
      <link
        key={item.url}
        rel="alternate"
        href={item.url}
        hrefLang={item.lng}
      />
    ));
  }, [altLinks]);

  /**
   * Título con fallback.
   */
  const pageTitle = useMemo(() => {
    return title;
  }, [title]);

  /**
   * Descripción con fallback.
   */
  const pageDescription = useMemo(() => {
    return description || "";
  }, [description]);

  /**
   * Meta tags adicionales para artículos.
   */
  const articleMeta = useMemo(() => {
    if (ogType !== OG_TYPE.ARTICLE || !extraMeta.article) {
      return null;
    }

    const { publishedTime, modifiedTime, author, section, tags } =
      extraMeta.article;

    return (
      <>
        {publishedTime && (
          <meta property="article:published_time" content={publishedTime} />
        )}
        {modifiedTime && (
          <meta property="article:modified_time" content={modifiedTime} />
        )}
        {author && <meta property="article:author" content={author} />}
        {section && <meta property="article:section" content={section} />}
        {tags?.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
      </>
    );
  }, [ogType, extraMeta.article]);

  /**
   * Meta tags adicionales para productos.
   */
  const productMeta = useMemo(() => {
    if (ogType !== OG_TYPE.PRODUCT || !extraMeta.product) {
      return null;
    }

    const { price, currency, availability } = extraMeta.product;

    return (
      <>
        {price !== undefined && (
          <meta property="product:price:amount" content={String(price)} />
        )}
        {currency && (
          <meta property="product:price:currency" content={currency} />
        )}
        {availability && (
          <meta property="product:availability" content={availability} />
        )}
      </>
    );
  }, [ogType, extraMeta.product]);

  /* --------------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------------ */

  return (
    <Helmet prioritizeSeoTags htmlAttributes={{ lang: i18n.resolvedLanguage }}>
      {/* Básicos */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={robots} />

      {/* Links */}
      {!isEmpty(canonicalUrl) && <link rel="canonical" href={canonicalUrl} />}
      {alternateLinks}

      {/* Open Graph - Básicos */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={appName} />
      <meta property="og:locale" content={ogLocale} />

      {/* Open Graph - Imagen */}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta
        property="og:image:width"
        content={String(DEFAULT_IMAGE_DIMENSIONS.width)}
      />
      <meta
        property="og:image:height"
        content={String(DEFAULT_IMAGE_DIMENSIONS.height)}
      />

      {/* Open Graph - Específicos por tipo */}
      {articleMeta}
      {productMeta}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:url" content={url} />
    </Helmet>
  );
}

export default SEO;
